import focusLock from 'dom-focus-lock'

import { awaitEvent, getEventPath, toArray } from './helpers'
class Revealer {

	static get defaults() {
		return {
			trigger:      undefined,
			await:        'animationend',
			hidden:       true,
			onBeforeShow: () => {},
			onAfterShow:  () => {},
			onBeforeHide: () => {},
			onAfterHide:  () => {}
		}
	}

	/**
	 * Create a revealer instance
	 * @param {HTMLElement}       element
	 * @param {RevealerDefaults}  config
	 */
	constructor(element, config) {
		this.element = element
		this.config = Object.assign({}, Revealer.defaults, config)

		// Bind this
		this.show = this.show.bind(this)
		this.hide = this.hide.bind(this)
		this.toggle = this.toggle.bind(this)

		this.init()
	}

	get hidden() {
		return this.element.hidden
	}

	set hidden(value) {
		this.element.hidden = value
	}

	/**
	 * Initialize
	 */
	init() {
		/** @type {HTMLElement[]} */
		this.trigger = toArray(
			(typeof this.config.trigger == 'string')
				? document.querySelectorAll(this.config.trigger)
				: this.config.trigger
		)

		if (this.config.hidden) {
			this.hide()
		} else {
			this.show()
		}

		if (this.trigger) {
			document.addEventListener('click', this.handleClick.bind(this))
		}
	}

	/**
	* Handle trigger clicks
	 * @param {MouseEvent} event
	 */
	handleClick(event) {
		const path = getEventPath(event)

		// Check event.path for any trigger elements
		if (this.trigger.find(trigger => path.includes(trigger))) {
			this.toggle(...arguments)
		}
	}

	/**
	 * Called before showing the element
	 * @param {Event|undefined} event
	 */
	beforeShow(event) {
		this.hidden = false
		this.lastFocus = document.activeElement
	}

	/**
	 * Called after showing the element
	 * @param {Event|undefined} event
	 */
	afterShow(event) {
		focusLock.on(this.element)
	}

	/**
	 * Shows the element
	 * @param {Event|undefined} event
	 */
	show(event) {
		if (!this.hidden) return

		if (event && event['preventDefault']) {
			event.preventDefault()
		}

		this.beforeShow(...arguments)
		this.config.onBeforeShow.apply(this, ...arguments)

		awaitEvent(this.element, this.config.await).then(() => {
			this.afterShow(...arguments)
			this.config.onAfterShow.apply(this, ...arguments)
		})
	}

	/**
	 * Called before hiding the element
	 * @param {Event|undefined} event
	 */
	beforeHide(event) {}

	/**
	 * Called after hiding the element
	 * @param {Event|undefined} event
	 */
	afterHide(event) {
		this.hidden = true
		focusLock.off(this.element)
		this.lastFocus.focus()
	}

	/**
	 * Hides the element
	 * @param {Event|undefined} event
	 */
	hide(event) {
		if (this.hidden) return

		if (event && event['preventDefault']) {
			event.preventDefault()
		}

		this.beforeHide(...arguments)
		this.config.onBeforeHide.apply(this, ...arguments)

		awaitEvent(this.element, this.config.await).then(() => {
			this.afterHide(...arguments)
			this.config.onAfterHide.apply(this, ...arguments)
		})
	}

	/**
	 * Toggle between show and hide
	 */
	toggle() {
		this.hidden ? this.show(...arguments) : this.hide(...arguments)
	}
}

/**
 * Mixin that extends the Revealer class, adding classnames to the element
 * @param  {Revealer} superclass
 * @return {Revealer}
 */
export function withClassnames(superclass) {

	/**
	 * @alias Revealer
	 */
	return class RevealWithClassnames extends superclass {
		static get defaults() {
			return Object.assign({}, superclass.defaults, {
				classNames: {
					open: 'is-open',
					opening: 'is-opening',
					closing: 'is-closing',
				}
			})
		}

		constructor(element, config) {
			super(element, Object.assign({}, RevealWithClassnames.defaults, config))
		}

		beforeShow() {
			super.beforeShow(...arguments)

			window.setTimeout(() => {
				this.element.classList.add(this.config.classNames.opening)
				this.trigger.forEach(trigger => {
					trigger.classList.add(this.config.classNames.open)
				})
			}, 10)
		}

		afterShow() {
			super.afterShow(...arguments)

			window.setTimeout(() => {
				this.element.classList.remove(this.config.classNames.opening)
				this.element.classList.add(this.config.classNames.open)
			}, 10)
		}

		beforeHide() {
			super.beforeHide(...arguments)

			this.element.classList.add(this.config.classNames.closing)
			this.trigger.forEach(trigger => {
				trigger.classList.remove(this.config.classNames.open)
			})
		}

		afterHide() {
			window.setTimeout(() => {
				this.element.classList.remove(this.config.classNames.closing)
				this.element.classList.remove(this.config.classNames.open)
			}, 10)

			super.afterHide(...arguments)
		}
	}
}

/**
 * Mixin that extends the Revealer class, adding basic a11y
 * @param  {Class} superclass
 * @return {Class}
 */
export function withA11y(superclass) {

	/**
	 * @alias Revealer
	 */
	return class extends superclass {
		init() {
			super.init()

			this.trigger.forEach(trigger => {
				trigger.setAttribute('aria-haspopup', true)
				trigger.setAttribute('aria-expanded', !this.hidden)
				trigger.setAttribute('aria-controls', this.element.id)
			})
		}

		get hidden() {
			if (this.element.hasAttribute('aria-hidden')) {
				return this.element.getAttribute('aria-hidden') === 'true'
			} else {
				return super.hidden
			}
		}

		set hidden(value) {
			if (this.element.hasAttribute('aria-hidden')) {
				this.element.setAttribute('aria-hidden', value)
			} else {
				super.hidden = value
			}
		}

		beforeShow(event) {
			super.beforeShow(...arguments)

			this.trigger.forEach(trigger => trigger.setAttribute('aria-expanded', true))
		}

		beforeHide() {
			super.beforeHide(...arguments)

			this.trigger.forEach(trigger => trigger.setAttribute('aria-expanded', false))
		}
	}
}

export function withClose(superclass) {

	return class RevealWithClose extends superclass {

		static get defaults() {
			return Object.assign({}, superclass.defaults, {
				closeTrigger: '[data-close]'
			})
		}

		constructor(element, config) {
			super(element, Object.assign({}, RevealWithClose.defaults, config))
		}

		handleClick(event) {
			super.handleClick(event)

			const close = [...this.element.querySelectorAll(this.config.closeTrigger)]
			const path = getEventPath(event)

			// Check event.path for any trigger elements
			if (close.find(close => path.includes(close))) {
				this.hide(...arguments)
			}
		}

		handleKeyDown(event) {
			if (event.key === 'Escape') {
				this.hide(...arguments)
			}
		}

		beforeShow(event) {
			super.beforeShow(...arguments)

			this.element.addEventListener('keydown', this.handleKeyDown.bind(this))
		}

		beforeHide() {
			super.beforeHide(...arguments)

			this.element.removeEventListener('keydown', this.handleKeyDown)
		}
	}
}

export default Revealer
